import styles from './login.module.css';
import './login.css';
import React, { useState, useEffect, useLayoutEffect, useRef } from "react"
import { notification } from 'antd';
import { GrCircleAlert } from 'react-icons/gr';
import { BiErrorCircle } from 'react-icons/bi';
import { useAuth } from "../../context/AuthProvider"
import { useHistory, useLocation, Link } from "react-router-dom"
import { useCustomer } from '../../context/CustomerProvider';

/** Default profile payload after email/password auth — same shape as legacy combined flow */
function buildEmailUserPayload(email) {
  return {
    full_name: null,
    contact_email: email,
    affiliate: null,
    lm_data: null
  };
}

/**
 * Avoid leaking whether an email is registered (user enumeration). For login,
 * use one generic message for wrong user / wrong password / invalid credential.
 */
function mapFirebaseAuthError(err, mode) {
  const code = err?.code;
  if (mode === 'login') {
    if (
      code === 'auth/user-not-found' ||
      code === 'auth/wrong-password' ||
      code === 'auth/invalid-credential' ||
      code === 'auth/invalid-email'
    ) {
      return 'Incorrect email or password.';
    }
    if (code === 'auth/user-disabled') {
      return 'This account has been disabled.';
    }
    if (code === 'auth/too-many-requests') {
      return 'Too many attempts. Try again later.';
    }
    return 'Something went wrong. Please try again.';
  }
  if (mode === 'signup') {
    if (code === 'auth/email-already-in-use') {
      // Do not confirm that the email exists; suggest the other flow without asserting it.
      return 'Could not create an account with these details. Try Log in, or use a different email.';
    }
    if (code === 'auth/weak-password') {
      return 'Password is too weak. Use at least 6 characters.';
    }
    if (code === 'auth/invalid-email') {
      return 'Please enter a valid email address.';
    }
    if (code === 'auth/too-many-requests') {
      return 'Too many attempts. Try again later.';
    }
    return 'Something went wrong. Please try again.';
  }
  return 'Something went wrong. Please try again.';
}

export default function Login() {
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const { firebase, currentUser, login, googleAuth, resetPassword, signup } = useAuth()
  const { setUser } = useCustomer()
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)
  const [pendingAction, setPendingAction] = useState(null) // 'login' | 'signup' | null
  const history = useHistory()
  const emailInputRef = useRef(null)



  // Access query parameters from URL
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  // Extract query parameters and set default values to null
  const fullName = queryParams.get("full_name") || null;
  const companyName = queryParams.get("company_name") || null;
  const lmref = queryParams.get("lmref") || null;
  const emailAddress = queryParams.get("email") || null;

  const openPopup = (message, description, icon, bg) => {
    notification.open({
      message,
      description,
      icon,
      placement: 'topRight',
      style: {'background-color' : bg}
    })
  }


  useEffect(() => {
    console.log("On Login page but already authenticated ")
    if (currentUser !== null){
      history.push('/');
    }
  }, []);

  /** Focus email: HTML autoFocus is flaky with SPA routing; layout effect + delayed retry beats focus steal. */
  useLayoutEffect(() => {
    const el = emailInputRef.current;
    if (!el) return;
    el.focus({ preventScroll: true });
    if (el.value) el.select();
  }, []);

  useEffect(() => {
    const focusEmail = () => {
      const el = emailInputRef.current;
      if (!el) return;
      el.focus({ preventScroll: true });
      if (el.value) el.select();
    };
    const t0 = window.setTimeout(focusEmail, 0);
    const t1 = window.setTimeout(focusEmail, 50);
    return () => {
      window.clearTimeout(t0);
      window.clearTimeout(t1);
    };
  }, []);


  useEffect(() => {
    // You can now use the extracted query parameter values (fullName, companyName, lmref) as needed.
    console.log("Full Name:", fullName);
    console.log("Company Name:", companyName);
    console.log("LMREF:", lmref);
    console.log("Email: ", emailAddress);

  }, [fullName, companyName, lmref, emailAddress]);


  async function persistUserAndRedirect(uid) {
    const data = buildEmailUserPayload(email);
    await setUser(uid, data);
    history.push("/");
  }

  async function handleLoginEmail() {
    setLoading(true);
    setPendingAction('login');
    setError("");

    try {
      const userCredential = await login(email, password);
      const uid = userCredential?.user?.uid || firebase.auth().currentUser?.uid;
      if (uid) {
        await persistUserAndRedirect(uid);
      }
    } catch (err) {
      console.log("Failed to log in: ", err);
      setError(mapFirebaseAuthError(err, 'login'));
    } finally {
      setLoading(false);
      setPendingAction(null);
    }
  }

  async function handleSignupEmail() {
    setLoading(true);
    setPendingAction('signup');
    setError("");

    try {
      const userCredential = await signup(email, password);
      console.log("userCredential (signup): ", userCredential.user.uid);
      const data = buildEmailUserPayload(email);
      console.log("About to call setUser after signup: ", data);
      await setUser(userCredential.user.uid, data);
      history.push("/");
    } catch (err) {
      console.log("Failed to sign up: ", err);
      setError(mapFirebaseAuthError(err, 'signup'));
    } finally {
      setLoading(false);
      setPendingAction(null);
    }
  }


  async function handleSubmitGoogle() {
    try {
      const status = await googleAuth();

      console.log("just returned ", status.user.uid)
      console.log("just returned entire google object: ", status.user)

      console.log("users name: ", status.user.displayName)
      console.log("users email: ", status.user.email)


      // Retrieve the referral information from the global 'window' object
      const lm_data = window.LinkMink('pub_live_2foBDbORbBKHAbQqSJC4').getReferralData();

      // Set lm_data to null if it's undefined or null
      const normalized_lm_data = lm_data || null;

      // Now you can use the lm_data as needed in your component
      console.log('Referral Data: ', normalized_lm_data);

      const data = {
        full_name: status.user.displayName,
        contact_email: status.user.email,
        affiliate: lmref,
        lm_data: normalized_lm_data
      }

      console.log("About to call setUser: ", status.user.uid, data)
      await setUser(status.user.uid, data)

      const codeAndStamp = JSON.parse(localStorage.getItem('item'))
      if(codeAndStamp){
        const {code, stamp} = codeAndStamp;
        history.push(`/invite/?code=${code}&timestamp=${stamp}`)
      }else{
        history.push("/")
      }

    } catch(e) {
      console.log("failed to login: ", e)
      setError("Failed to log in")
    }
  }


  const handleResetPass = async () => {
    console.log('Button clicked!');
    const emailRegex = /\S+@\S+\.\S+/;

    try {
      if (!emailRegex.test(email)) {
          openPopup('Invalid Email Format or Email Not Available.', "Please input the email connected to your account and we'll email you a link to reset your password.", <BiErrorCircle/>, '#FF7276 ')
      } else {
        const reset = await resetPassword(email);
        console.log("Reset: ", reset)

        openPopup('Reset Password Email Sent!.',`We have send the link to reset your password over at ${email}`, <GrCircleAlert/>, '#EEE')
      }
    } catch (error) {
      console.log(error.message);
      openPopup('User does not exist', error.message , <BiErrorCircle/>, '#FF7276 ')
    }
}

  return (
    <>
      <div className="container-beginning">
        <div className="centered-beginning">

              <div className={styles.login}>
                <img
                  src="/logo.svg"
                  alt="Icarus AI"
                  className={styles.brandLogo}
                  width={53}
                  height={50}
                />
                <h3 className={styles.title}>AICS by Icarus AI</h3>
                <p className={styles.parr}>
                  Log in to your workspace.
                </p>

                <button type="button" className={styles.button} onClick={handleSubmitGoogle}>
                  <div className={styles.buttoncontent}>
                    <img src="google.png" alt="Google logo" className={styles.buttonlogo} />
                    <p className={styles.buttontext}>Continue with Google</p>
                  </div>
                </button>

                  <div className={styles.headerOr}>OR</div>

                  <form
                    className={styles.loginform}
                    name="basic"
                    autoComplete="on"
                    onSubmit={(e) => {
                      e.preventDefault();
                      if (loading) return;
                      handleLoginEmail();
                    }}
                  >
                    <input
                      ref={emailInputRef}
                      className={styles.input}
                      type="email"
                      name="email"
                      placeholder="Email address"
                      aria-label="Email address"
                      onChange={(e) => setEmail(e.target.value)}
                      value={email}
                      required
                      autoComplete="email"
                    />

                    <input
                      className={styles.input}
                      type="password"
                      name="password"
                      placeholder="Password"
                      aria-label="Password"
                      onChange={(e) => setPassword(e.target.value)}
                      value={password}
                      required
                      autoComplete="current-password"
                    />

                    {error ? (
                      <p className={styles.authError} role="alert">
                        {error}
                      </p>
                    ) : null}

                    <div className={styles.authButtonRow}>
                      <button
                        type="button"
                        className={styles.submitbutton}
                        disabled={loading}
                        onClick={handleLoginEmail}
                        aria-busy={pendingAction === 'login'}
                      >
                        {pendingAction === 'login' ? 'Signing in…' : 'Log in'}
                      </button>
                    </div>
                  </form>

                  <p className={styles.parr}>
                    By signing in, you agree to our{' '}
                    <Link
                      to="/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.policyLink}
                    >
                      Privacy Policy
                    </Link>
                    .
                    {' '}If you forgot your password,{' '}
                    <button type="button" onClick={handleResetPass} className={styles.buttonlink}>
                      reset it
                    </button>
                    .
                  </p>

              </div>

        </div>
      </div>
    </>
  )
}
